import { toSignal } from '@angular/core/rxjs-interop';
// Common
import { Component, inject, Input, OnInit, SimpleChanges } from '@angular/core';
import { type HttpContext } from '@angular/common/http';
import { contactsOwnerContext } from '../../services/contacts-owner-context';
import { ContactsTenantScopeService } from '../../services/contacts-tenant-scope.service';
import { heightAnimation } from '@modules/common/animations/height.animation';

// Services
import { ContactsService } from '@modules/contacts/services/contacts.service';
import { GroupsService } from '@modules/contacts/services/groups.service';

// Types
import { Application } from '@modules/common/types/application';
import { Stitch } from '@modules/common/types/stitch';
import { Contact } from '@modules/contacts/types/contact';
import { Group } from '@modules/contacts/types/group';
import { GroupsFilters } from '@modules/contacts/types/groups-filters';
import { VirtualFolder } from '@modules/contacts/types/virtual-folder';
import { DragData, dragDataTypeAllowed, DragDataTypes, getStringField } from '@modules/drag-n-drop/types/drag-data';
import { ToasterService } from '@frontendlib/common/features/toaster/services/toaster.service';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { CROSS_TENANT_DROP_TEXT } from '@modules/common/services/container-dnd.service';
import { StateKey } from '@modules/settings/types/state-key';
import type { ContactSummaryDto } from '@sharedlib/dtos/dto/contacts-app/contact.dto';
import type { GroupSummaryDto } from '@sharedlib/dtos/dto/contacts-app/group.dto';

// RxJs
import { filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, merge, of } from 'rxjs';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';
import { StitchEventsService } from '@modules/common/services/stitch-events-service';
import { SignalStitchEventType } from '@sharedlib/common/types/signal-type';
import { StateService } from '@modules/settings/services/state.service';
import { APPLICATION_SCOPE_TOKEN, ApplicationScope } from '@modules/common/types/ApplicationScope';

// Components
import { BaseSidebarContainersTreeComponent } from '@modules/common/components/base-sidebar-containers-tree/base-sidebar-containers-tree.component';
import { moveItemsIntoContainer } from '@modules/common/utils/move-into-container.util';

@Component({
  selector: 'app-side-bar-groups-tree',
  templateUrl: './side-bar-groups-tree.component.html',
  styleUrls: ['./side-bar-groups-tree.component.less'],
  animations: [heightAnimation],
  standalone: false,
})
export class SideBarGroupsTreeComponent
  extends BaseSidebarContainersTreeComponent<Group, Contact, GroupsFilters, VirtualFolder>
  implements OnInit
{
  selfDragDataTypes: DragDataTypes[] = [DragDataTypes.contact, DragDataTypes.group];
  containerDragDataType = DragDataTypes.group;
  application = Application.contacts;
  private readonly hostScope = inject(APPLICATION_SCOPE_TOKEN, { optional: true }) ?? ApplicationScope.MAIN;
  private readonly savedState = inject(StateService);
  public override get treeStateKey(): StateKey {
    const key = `${StateKey.contactsSidebarGroupsTree}.${this.hostScope}.${this.rowOwner() ?? 'personal'}` as StateKey;
    if (
      this.savedState.getStateSync(key) === undefined &&
      this.contactsScope.canAdoptLegacyTeamState(this.rowOwner())
    ) {
      const legacy = this.savedState.getStateSync(StateKey.contactsSidebarGroupsTree);
      if (legacy !== undefined) this.savedState.setState(key, legacy);
    }
    return key;
  }
  @Input() public tenantId: string | null | undefined;
  private readonly contactsScope = inject(ContactsTenantScopeService);
  private readonly settledTenant = toSignal(this.contactsScope.selectedTeamId(), { initialValue: undefined });

  private readonly rowChanged = new BehaviorSubject<void>(undefined);
  private readonly ownSearch = inject(AdvancedSearchService);
  private readonly ownEvents = inject(StitchEventsService);
  private reconcilingSelection = false;
  private treeLoaded = false;

  private rowOwner(): string | null {
    return this.tenantId === undefined ? (this.team?.id ?? this.settledTenant()) : this.tenantId;
  }

  public override ngOnInit(): void {
    this.ownEvents
      .getEvents(() => [this.rowOwner()], StitchType.GROUP, SignalStitchEventType.UPDATED)
      .pipe(takeUntil(this.alive))
      .subscribe((payload) => {
        const ids = new Set(payload.ids);
        for (const item of this.items) if (ids.has(item.id)) Object.assign(item, payload.changes);
      });
  }

  public override ngOnChanges(changes: SimpleChanges): void {
    if ('filters' in changes || 'tenantId' in changes || 'team' in changes) this.rowChanged.next();
  }

  /** Contacts owns this subscription because both trees remain mounted during an area change. */
  public override ngAfterViewInit(): void {
    this.selectableList()
      ?.selectableService.getSelectedItems()
      .pipe(
        filter(() => this.treeLoaded && !this.reconcilingSelection),
        takeUntil(this.alive),
      )
      .subscribe((items) => {
        if (items.length) this.beforeContainerSelectionApplied();
        else if (this.rowOwner() !== this.contactsScope.effectiveTenantId()) return;
        const state = this.ownSearch.getStateSync();
        state.contacts.containersIds = items.map((item) => item.identifier);
        state.contacts.containerName =
          this.items.find((item) => item.id === state.contacts.containersIds[0])?.title ?? null;
        this.ownSearch.setState(state);
      });

    combineLatest([
      combineLatest([this.ownSearch.getState(), this.contactsScope.selectedTeamId(), this.rowChanged]).pipe(
        map(([state, owner]) => (owner === this.rowOwner() ? (state.contacts.containersIds ?? []) : [])),
      ),
      merge(this.rowChanged, this.groupsService.getRefresh()).pipe(
        tap(() => {
          this.items = [];
        }),
        switchMap(() =>
          this.tenantId !== undefined || this.team?.id ? of(this.rowOwner()) : this.contactsScope.selectedTeamId(),
        ),
        filter(() => !!this.filters),
        switchMap(() =>
          this.groupsService.findAll(
            { ...this.filters, deleted: false, archived: false, snoozedOnTop: false, followedOnTop: false },
            { requestContext: this.requestContext() },
          ),
        ),
      ),
    ])
      .pipe(takeUntil(this.alive))
      .subscribe(([ids, items]) => {
        this.items = items;
        this.selectedItemsIds = ids.filter((id) => items.some((item) => item.id === id));
        this.treeLoaded = true;
        const selection = this.selectableList()?.selectableService;
        if (selection) {
          const current = selection.getSelectedItemsSync();
          const next = current.filter((item) => this.selectedItemsIds.includes(item.identifier));
          if (next.length !== current.length) {
            this.reconcilingSelection = true;
            try {
              selection.setSelectedItems(next);
            } finally {
              this.reconcilingSelection = false;
            }
          }
        }
        if (this.rowOwner() === this.contactsScope.effectiveTenantId()) {
          const state = this.ownSearch.getStateSync();
          const opened = items.find((item) => item.id === state.contacts.containersIds?.[0]);
          if (opened && state.contacts.containerName !== opened.title) {
            state.contacts.containerName = opened.title;
            this.ownSearch.setState(state);
          }
        }
        this.onItemsReceived();
      });
  }

  protected override requestContext(): HttpContext | undefined {
    return contactsOwnerContext(
      undefined,
      this.tenantId === undefined ? (this.team?.id ?? this.settledTenant()) : this.tenantId,
    );
  }

  protected override beforeContainerSelectionApplied(): void {
    const owner = this.tenantId === undefined ? this.team?.id : this.tenantId;
    if (owner !== undefined) this.contactsScope.selectTenant(owner);
  }

  dndPredicate =
    (stitchItem: Stitch) =>
    (dragData: DragData): boolean =>
      stitchItem &&
      !(
        dragData.type === DragDataTypes.group &&
        dragData.data.length === 1 &&
        dragData.data?.[0]?.id === stitchItem?.id
      ) &&
      dragDataTypeAllowed(dragData.type) &&
      !this.isCrossTenantDrop(dragData, stitchItem);

  private readonly toaster = inject(ToasterService);

  private isCrossTenantDrop(data: DragData, destination: Stitch): boolean {
    if (!this.selfDragDataTypes.includes(data.type)) return false;
    const owner = destination.accountId ?? this.tenantId ?? this.team?.id;
    return (
      !!owner &&
      data.data.some((item) => {
        const sourceOwner = getStringField(item, 'accountId');
        return sourceOwner !== undefined && sourceOwner !== owner;
      })
    );
  }

  private refuseCrossTenantDrop(data: DragData, destination: Stitch): boolean {
    if (!this.isCrossTenantDrop(data, destination)) return false;
    this.toaster.show({ text: CROSS_TENANT_DROP_TEXT.moveTooltip });
    return true;
  }

  public override dndDrop(data: DragData, destination: Stitch): void {
    if (this.refuseCrossTenantDrop(data, destination)) return;
    super.dndDrop(data, destination);
  }

  public override handleStitch(data: DragData, destination: Stitch): void {
    if (this.refuseCrossTenantDrop(data, destination)) return;
    super.handleStitch(data, destination);
  }

  constructor(
    private groupsService: GroupsService,
    private contactsService: ContactsService,
  ) {
    super(groupsService);
  }

  /**
   * Actions
   */

  public handleMove(dragData: DragData, item: Group) {
    if (this.refuseCrossTenantDrop(dragData, item)) return;
    const message = 'Successfully moved to ' + item.title;

    if (dragData.type === DragDataTypes.contact) {
      const ids = this.getIds(dragData.data);
      moveItemsIntoContainer(this.contactsService, dragData.data, () =>
        this.contactsService.bunchUpdate(
          { ids },
          { groupId: item.id },
          { message, tenantItems: dragData.data as ContactSummaryDto[] },
        ),
      );
    } else if (dragData.type === DragDataTypes.group) {
      moveItemsIntoContainer(this.groupsService, dragData.data, () =>
        this.groupsService.bunchUpdate(
          { ids: this.getIds(dragData.data) },
          { parentId: item.id },
          { message, tenantItems: dragData.data as GroupSummaryDto[] },
        ),
      );
    }
  }

  public handleDuplicate(dragData: DragData, group: Group) {
    if (this.refuseCrossTenantDrop(dragData, group)) return;
    if (dragData.type === DragDataTypes.contact) {
      this.contactsService
        .duplicate(dragData.data as ContactSummaryDto[], group.id)
        .pipe(take(1))
        .subscribe();
    } else if (dragData.type === DragDataTypes.group) {
      this.groupsService
        .duplicate(dragData.data as GroupSummaryDto[])
        .pipe(take(1))
        .subscribe();
    }
  }
}
